"use client";
import React, { useState, useCallback, useEffect, useRef } from "react";
import Breadcrumb from "./Breadcrumb";
import { X, Filter } from "lucide-react";
import { useColors } from "../../context/ColorContext";
import { useFabrics } from "../../context/FabricContext";
import { useCategories } from "../../context/CategoryContext";


/* ---------------------- PRICE RANGE SLIDER ---------------------- */
const PriceRangeSlider = ({ min, max, value, onChange }) => {
  const [isDragging, setIsDragging] = useState(null);
  const sliderRef = useRef(null);

  const handleMouseDown = useCallback((thumb) => setIsDragging(thumb), []);
  const handleMouseUp = useCallback(() => setIsDragging(null), []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || !sliderRef.current) return;
      const slider = sliderRef.current.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (e.clientX - slider.left) / slider.width));
      const newValue = Math.round(min + percent * (max - min));

      if (isDragging === "min") {
        onChange([Math.min(newValue, value[1]), value[1]]);
      } else {
        onChange([value[0], Math.max(newValue, value[0])]);
      }
    },
    [isDragging, min, max, value, onChange]
  );

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const minPercent = ((value[0] - min) / (max - min)) * 100;
  const maxPercent = ((value[1] - min) / (max - min)) * 100;

  return (
    <div className="px-4 py-6">
      <div ref={sliderRef} className="relative h-2 bg-gray-200 rounded-lg">
        <div
          className="absolute h-2 bg-yellow-500 rounded-lg"
          style={{ left: `${minPercent}%`, width: `${maxPercent - minPercent}%` }}
        />
        {/* Min Thumb */}
        <div
          className="absolute w-5 h-5 bg-yellow-500 border-2 border-white cursor-pointer shadow-md transform -translate-y-1.5 rounded-full focus:ring-2 focus:ring-yellow-500"
          style={{ left: `${minPercent}%`, marginLeft: "-10px" }}
          onMouseDown={() => handleMouseDown("min")}
        />
        {/* Max Thumb */}
        <div
          className="absolute w-5 h-5 bg-yellow-500 border-2 border-white cursor-pointer shadow-md transform -translate-y-1.5 rounded-full focus:ring-2 focus:ring-yellow-500"
          style={{ left: `${maxPercent}%`, marginLeft: "-10px" }}
          onMouseDown={() => handleMouseDown("max")}
        />
      </div>

      {/* Display Values */}
      <div className="flex justify-between mt-4 text-sm font-medium text-neutral-700">
        <span>₹ {value[0].toLocaleString()}</span>
        <span>₹ {value[1].toLocaleString()}</span>
      </div>
    </div>
  );
};

/* ---------------------- MAIN FILTER SIDEBAR ---------------------- */
const FilterSidebar = ({ filters, onFiltersChange, isOpen, onToggle }) => {
  const { colors, loading: colorLoading } = useColors();
  const { fabrics, loading: fabricLoading } = useFabrics();
  const { categories, loading: categoryLoading } = useCategories();

  // UI-only: controls subcategory visibility
  const [visibleSubcategories, setVisibleSubcategories] = useState({});

  const [expandedSections, setExpandedSections] = useState({
    price: true,
    categories: true,
    color: true,
    fabric: true,
  });

  const [expandedCategories, setExpandedCategories] = useState({});

  /* ------------ HANDLERS ------------ */
  const updateFilter = useCallback(
    (key, value) => {
      onFiltersChange({ ...filters, [key]: value });
    },
    [filters, onFiltersChange]
  );

  const handlePriceChange = (range) => updateFilter("priceRange", range);

  const handleCategoryToggle = (name) => {
    const updated = filters.categories.includes(name)
      ? filters.categories.filter((c) => c !== name)
      : [...filters.categories, name];
    updateFilter("categories", updated);
  };

  const handleSubcategoryToggle = (categoryName, subName) => {
    let updated = [...filters.categories];

    // toggle subcategory
    if (updated.includes(subName)) {
      updated = updated.filter((v) => v !== subName);
    } else {
      updated.push(subName);
    }

    // ❗ remove parent category if subcategory is selected
    updated = updated.filter((v) => v !== categoryName);

    updateFilter("categories", updated);
  };


  const handleCategoryCheckbox = (category) => {
  // keep your existing filter logic
  handleCategoryToggle(category.name);

  // UI-only toggle
  setVisibleSubcategories((prev) => ({
    ...prev,
    [category.id]: !prev[category.id],
  }));
};


  const handleColorToggle = (name) => {
    const updated = filters.colors.includes(name)
      ? filters.colors.filter((c) => c !== name)
      : [...filters.colors, name];
    updateFilter("colors", updated);
  };

  const handleFabricToggle = (name) => {
    const updated = filters.fabrics.includes(name)
      ? filters.fabrics.filter((f) => f !== name)
      : [...filters.fabrics, name];
    updateFilter("fabrics", updated);
  };

  const toggleSection = (key) =>
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleCategoryExpansion = (id) =>
    setExpandedCategories((prev) => ({ ...prev, [id]: !prev[id] }));

  const clearAllFilters = () => {
    onFiltersChange({
      categories: [],
      colors: [],
      fabrics: [],
      priceRange: [0, 200000],
    });
  };

  /* ------------ EFFECTS ------------ */
  useEffect(() => {
    if (filters.categories?.length > 0) {
      setExpandedSections((prev) => ({ ...prev, categories: true }));
    }
  }, [filters.categories]);

  useEffect(() => {
    if (filters.colors?.length > 0) {
      setExpandedSections((prev) => ({ ...prev, color: true }));
    }
  }, [filters.colors]);

  useEffect(() => {
    if (filters.fabrics?.length > 0) {
      setExpandedSections((prev) => ({ ...prev, fabric: true }));
    }
  }, [filters.fabrics]);
  const currentPriceRange = filters.priceRange || [0, 200000];

  /* ------------ RENDER ------------ */
  return (
    <>
      {/* Mobile Filter Toggle */}
      <button
        className="lg:hidden fixed top-36 right-4 z-50 bg-yellow-500 text-white p-3 rounded-full shadow-lg hover:bg-yellow-600"
        onClick={onToggle}
      >
        <Filter size={22} />
      </button>

      {isOpen && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onToggle} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static top-0 left-0 h-full lg:h-auto bg-white transform lg:transform-none transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        } w-80 lg:w-[245px] z-50 lg:z-auto overflow-y-auto pb-24 px-4 pt-4`}
      >
        <button
          className="lg:hidden absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
          onClick={onToggle}
        >
          <X size={24} />
        </button>

        <div className="pt-8 pb-2 w-full font-medium">
          <Breadcrumb />
        </div>

        <div className="flex items-center justify-between pt-6 pb-4 text-sm font-medium text-neutral-700">
          <h2>Filters</h2>
          {(filters.categories.length > 0 ||
            filters.colors.length > 0 ||
            filters.fabrics.length > 0 ||
            filters.priceRange[0] !== 0 ||
            filters.priceRange[1] !== 200000) && (
            <button
              onClick={clearAllFilters}
              className="text-xs text-yellow-600 hover:text-yellow-700"
            >
              Clear All
            </button>
          )}
        </div>

        {/* ---------------------- PRICE FILTER ---------------------- */}
        <section className="border-b border-stone-300">
          <button
            className="flex justify-between items-center w-full py-3 hover:bg-gray-50"
            onClick={() => toggleSection("price")}
          >
            <h3 className="text-sm font-medium">Price</h3>
            <img
              src="https://api.builder.io/api/v1/image/assets/TEMP/936e27b144555752e9778ab5cb66109b259484b3"
              className={`w-4 h-4 transition-transform ${
                expandedSections.price ? "rotate-180" : ""
              }`}
              alt=""
            />
          </button>
          {expandedSections.price && (
            <PriceRangeSlider
              min={0}
              max={200000}
              value={currentPriceRange}
              onChange={handlePriceChange}
            />
          )}
        </section>

        {/* ---------------------- CATEGORY FILTER ---------------------- */}
        <section className="border-b border-stone-300">
          <button
            className="flex justify-between items-center w-full py-3 hover:bg-gray-50"
            onClick={() => toggleSection("categories")}
          >
            <h3 className="text-sm font-medium">Categories</h3>
            <img
              src="https://api.builder.io/api/v1/image/assets/TEMP/2d75c2114313ec68f22ff6419fe11e387830a1f3"
              className={`w-4 h-4 transition-transform ${
                expandedSections.categories ? "rotate-180" : ""
              }`}
              alt=""
            />
          </button>

          {expandedSections.categories && (
            <div className="max-h-[400px] overflow-y-auto pl-2">
              {categoryLoading ? (
                <div className="text-sm text-gray-500 p-4">Loading categories...</div>
              ) : (
                categories.map((category) => (
                  <div key={category.id} className="mb-3">
                    {/* Main Category */}
                    <div className="flex justify-between items-center px-4 py-2">
                      <div className="flex items-center gap-3">
                        <input
  type="checkbox"
  id={`category-${category.id}`}
  checked={filters.categories.includes(category.name)}
  onChange={() => handleCategoryCheckbox(category)}
  className="w-4 h-4 text-yellow-600 border-gray-300 rounded"
/>

                        <label htmlFor={`category-${category.id}`} className="text-sm">
                          {category.name}
                        </label>
                      </div>

                      {/* {category.subcategories?.length > 0 && (
                        <button onClick={() => toggleCategoryExpansion(category.id)}>
                          <img
                            src="https://api.builder.io/api/v1/image/assets/TEMP/936e27b144555752e9778ab5cb66109b259484b3"
                            className={`w-3 h-3 transition-transform ${
                              expandedCategories[category.id] ? "rotate-180" : ""
                            }`}
                            alt=""
                          />
                        </button>
                      )} */}
                    </div>

                    {/* Subcategories */}
                    {category.subcategories &&
                      category.subcategories.length > 0 &&
                      visibleSubcategories[category.id] && (
                        <div className="ml-8 mt-1">
                          {category.subcategories.map((sub) => (
                            <div key={sub.id} className="flex items-center gap-3 py-1">
                              <input
                                type="checkbox"
                                id={`sub-${sub.id}`}
                                checked={filters.categories.includes(sub.sub_cat_name)}
                                onChange={() => handleSubcategoryToggle(category.name, sub.sub_cat_name)}
                                className="w-3 h-3 text-yellow-600 border-gray-300 rounded"
                              />
                              <label htmlFor={`sub-${sub.id}`} className="text-xs text-neutral-600">
                                {sub.sub_cat_name}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                ))
              )}
            </div>
          )}
        </section>

        {/* ---------------------- COLOR FILTER ---------------------- */}
        <section className="border-b border-stone-300">
          <button
            className="flex justify-between items-center w-full py-3 hover:bg-gray-50"
            onClick={() => toggleSection("color")}
          >
            <h3 className="text-sm font-medium">Color</h3>
            <img
              src="https://api.builder.io/api/v1/image/assets/TEMP/17af16a5951ae2e5d4144806745cbdc2612106cb"
              className={`w-4 h-4 transition-transform ${
                expandedSections.color ? "rotate-180" : ""
              }`}
              alt=""
            />
          </button>

          {expandedSections.color && (
            <div className="max-h-[250px] overflow-y-auto pl-2">
              {colorLoading ? (
                <div className="text-sm text-gray-500 p-4">Loading colors...</div>
              ) : (
                colors.map((color) => (
                  <div key={color.id} className="flex items-center gap-3 px-4 py-2">
                    <input
                      type="checkbox"
                      id={`color-${color.id}`}
                      checked={filters.colors.includes(color.name)}
                      onChange={() => handleColorToggle(color.name)}
                      className="w-4 h-4 text-yellow-600 border-gray-300 rounded"
                    />
                    <label htmlFor={`color-${color.id}`} className="text-sm text-neutral-700">
                      {color.name}
                    </label>
                  </div>
                ))
              )}
            </div>
          )}
        </section>

        {/* ---------------------- FABRIC FILTER ---------------------- */}
        <section>
          <button
            className="flex justify-between items-center w-full py-3 hover:bg-gray-50"
            onClick={() => toggleSection("fabric")}
          >
            <h3 className="text-sm font-medium">Fabric</h3>
            <img
              src="https://api.builder.io/api/v1/image/assets/TEMP/8deccba0f9aebe020532e38ec9b7374ed6a33dce"
              className={`w-4 h-4 transition-transform ${
                expandedSections.fabric ? "rotate-180" : ""
              }`}
              alt=""
            />
          </button>

          {expandedSections.fabric && (
            <div className="max-h-[250px] overflow-y-auto pl-2">
              {fabricLoading ? (
                <div className="text-sm text-gray-500 p-4">Loading fabrics...</div>
              ) : (
                fabrics.map((fabric) => (
                  <div key={fabric.id} className="flex items-center gap-3 px-4 py-2">
                    <input
                      type="checkbox"
                      id={`fabric-${fabric.id}`}
                      checked={filters.fabrics.includes(fabric.name)}
                      onChange={() => handleFabricToggle(fabric.name)}
                      className="w-4 h-4 text-yellow-600 border-gray-300 rounded"
                    />
                    <label htmlFor={`fabric-${fabric.id}`} className="text-sm text-neutral-700">
                      {fabric.name}
                    </label>
                  </div>
                ))
              )}
            </div>
          )}
        </section>
      </aside>
    </>
  );
};

export default FilterSidebar;
